@file:Suppress("MagicNumber", "NestedBlockDepth", "LongMethod", "TooManyFunctions", "CyclomaticComplexMethod")

package top.wcpe.allincore.ui.runtime.event

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.Modal
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.concurrent.ThreadLocalRef
import top.wcpe.allincore.ui.runtime.render.RenderContext
import top.wcpe.allincore.ui.runtime.render.overlayOrchestrator
import top.wcpe.allincore.ui.runtime.render.transform.AffineMatrix3
import top.wcpe.allincore.ui.runtime.render.transform.Transform2D
import kotlin.jvm.JvmStatic

/**
 * UI 树命中测试与 overlay 命中扩展。
 * 同时维护 [Transform2D]（向后兼容包围盒）与 [AffineMatrix3]（旋转/倾斜命中测试）。
 * 遇到 pointerEvents == false 的元素跳过该子树。
 */
internal object HitTester {

    // ===== ThreadLocal 状态 =====

    /**
     * 当前帧 root 在屏幕坐标系下的偏移量（主元素树居中渲染产生）。
     *
     * UIRuntimeScreen 的 mouseClicked 在调 dispatchClick 之前会把 mouseX/Y 减去 rootOffset 得到 root 局部坐标，
     * 但 fullscreen modal 子树用屏幕坐标渲染——HitTester 进入 modal 子树命中时需要把 mx/my 加回 rootOffset 还原屏幕坐标，
     * 才能与 modal 子节点的渲染位置对齐。
     *
     * 由调用方（UIRuntimeScreen.mouseClicked / HudInputHook 等）每次 dispatch 前 set，dispatch 完后 clear，避免脏数据残留。
     */
    /** [currentRootOffset] 的可变槽位容器（[ThreadLocalRef] 仅暴露 get()，以容器字段承载可写值）。 */
    private class RootOffsetHolder {
        var value: FloatArray? = null
    }

    private val currentRootOffset: ThreadLocalRef<RootOffsetHolder> = ThreadLocalRef { RootOffsetHolder() }

    /**
     * 当前命中线程的「全局元素 box 查询函数」——按元素 id 返回屏幕绝对坐标 + 尺寸（float[4]）。
     *
     * 用途：spotlight modal 的目标元素在本 session root 树之外（如引导 HUD spotlight 到
     * 另一个 HUD 的元素）时，浮层编排缝的 findModalElementBox 在本 root DFS 找不到，命中侧
     * 必须能跨 session 查询屏幕绝对坐标。与渲染侧 [RenderContext.elementBox]
     * 同口径，由平台层（如 fabric HudRenderHook / UIRuntimeScreen）在 dispatch 前注入。
     */
    /** [currentElementBoxLookup] 的可变槽位容器（承载可写的 elementBox 查询函数）。 */
    private class ElementBoxLookupHolder {
        var value: ((String) -> FloatArray?)? = null
    }

    private val currentElementBoxLookup: ThreadLocalRef<ElementBoxLookupHolder> =
        ThreadLocalRef { ElementBoxLookupHolder() }

    // ===== 公开 API（由 InputDispatcher 转发） =====

    /** 设置当前命中线程的 root 屏幕偏移，dispatch 完成后必须 clear。 */
    @JvmStatic
    fun setRootOffset(x: Float, y: Float) {
        currentRootOffset.get().value = floatArrayOf(x, y)
    }

    /** 清除当前线程的 root 屏幕偏移。 */
    @JvmStatic
    fun clearRootOffset() {
        currentRootOffset.get().value = null
    }

    /** 设置当前命中线程的全局 elementBox 查询函数；与 setRootOffset 配对使用，dispatch 完后必须 clear。 */
    @JvmStatic
    fun setElementBoxLookup(lookup: ((String) -> FloatArray?)?) {
        currentElementBoxLookup.get().value = lookup
    }

    /** 清除当前线程的 elementBox 查询函数。 */
    @JvmStatic
    fun clearElementBoxLookup() {
        currentElementBoxLookup.get().value = null
    }

    /** 内部：跨 session 查询屏幕绝对坐标，未注入 lookup 时返回 null。 */
    @JvmStatic
    fun lookupElementBoxGlobal(elementId: String): FloatArray? {
        val fn: (String) -> FloatArray? = currentElementBoxLookup.get().value ?: return null
        return fn(elementId)
    }

    // ===== 内部辅助 =====

    /** 当前命中线程的 root 屏幕偏移 X；未设置时为 0。模块内可见，供 [ControlInteractions] 折叠判定复用。 */
    internal fun rootOffsetX(): Float = currentRootOffset.get().value?.getOrNull(0) ?: 0f

    /** 当前命中线程的 root 屏幕偏移 Y；未设置时为 0。模块内可见，供 [ControlInteractions] 折叠判定复用。 */
    internal fun rootOffsetY(): Float = currentRootOffset.get().value?.getOrNull(1) ?: 0f

    // ===== 主入口 =====

    @JvmStatic
    fun hit(
        root: UIElement<*>,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        path: MutableList<UIElement<*>>,
        absBoxes: MutableList<FloatArray>?,
    ): Boolean {
        // M6-h Round 1：先做 modal 层命中测试。任何 visible 的 modal 都会「吸收」事件，
        // 下层 root 树不再参与命中。命中 modal 子树则返回该子树路径；命中 backdrop（modal
        // 子树外）时返回 modal 自身——若 modal 设置了 onDismiss handler，dismissOnOutsideClick
        // 触发；未设置则 dispatcher 自然返回 false（事件被吸收但无副作用）。
        // Stage 14：passthrough modal 在子树未命中时不吸收事件，回落到主 root 树。
        // 阶段 14 增强：spotlight modal（passthrough + spotlightTarget）只让「洞内」点击穿透，
        // 遮罩区域吸收事件——把 root 传给 hitModalLayer 用于反查 spotlight 目标几何。
        val modals = overlayOrchestrator().collectVisibleModals(root)
        if (modals.isNotEmpty()) {
            if (hitModalLayer(modals, layout, mx, my, path, absBoxes, root)) {
                return true
            }
            // 所有 passthrough modal 都未命中子树 → 回落主 root 树
        }
        return hit(
            root, layout, 0f, 0f,
            Transform2D.IDENTITY, AffineMatrix3.IDENTITY,
            mx, my, path, absBoxes,
        )
    }

    // ===== Modal 层命中 =====

    /**
     * Modal 层命中测试入口。
     *
     * 逆序遍历 modal 列表（顶层优先）：
     * - 命中 modal 子树 → 写入 path 返回 true（事件由 modal 吸收）
     * - 未命中子树但 modal 是 passthrough → 跳过该 modal 继续测下一个；
     *   所有 passthrough modal 都未命中时 hitModalLayer 返回 false，由调用方回落主 root 树
     * - 未命中子树且 modal 非 passthrough → 走 backdrop 命中（旧逻辑），事件被吸收
     *
     * @return true 表示事件被 modal 层处理；false 表示需要回落主 root 树
     */
    @Suppress("CyclomaticComplexMethod")
    private fun hitModalLayer(
        modals: List<Modal>,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        path: MutableList<UIElement<*>>,
        absBoxes: MutableList<FloatArray>?,
        root: UIElement<*>,
    ): Boolean {
        // 第一遍：尝试命中所有 modal 的子树（顶层优先）
        for (i in modals.indices.reversed()) {
            val modal = modals[i]
            if (hitModalSubtree(modal, layout, mx, my, path, absBoxes)) {
                return true
            }
            // 子树未命中：先做 spotlight 检查（如果启用）——
            // passthrough + spotlight 的「穿透」语义只对「洞区域」生效，遮罩区域必须吸收事件。
            // 这样玩家不能点到遮罩下的业务按钮，符合业界 spotlight 标准。
            val targetId = modal.spotlightTargetId()
            if (!targetId.isNullOrEmpty()) {
                var inHole: Boolean? = isInsideSpotlightHole(modal, layout, mx, my, root)
                if (inHole == null) {
                    // 找不到目标元素：保守按「洞外」处理——不穿透，吸收事件
                    inHole = false
                }
                if (!inHole) {
                    // 点击落在遮罩区域（洞外）：吸收事件，不走 passthrough 主树
                    return true
                }
                // 点击落在洞内：继续走 passthrough 路径回落主树
            }
            // 子树未命中：passthrough modal 跳过；非 passthrough modal 进入 backdrop 处理
            if (!modal.passthrough()) {
                // 非 passthrough：按旧逻辑走 backdrop（吸收事件 + 可选 dismiss）
                handleNonPassthroughBackdrop(modal, layout, path, absBoxes)
                return true
            }
        }
        // 所有 modal 都是 passthrough 且子树都未命中 → 不吸收事件，让调用方回落主 root 树
        return false
    }

    /** 仅尝试命中 modal 子树（不走 backdrop）；命中返回 true 并写入 path。
     * fullscreen modal 子树用屏幕坐标渲染，而入口 mx/my 是主元素树的 root 局部坐标
     * （UIRuntimeScreen.mouseClicked 已减去 rootOffset）；命中前需要把 mx/my 加回 rootOffset
     * 还原成屏幕坐标，才能与 modal 子节点的实际渲染位置对齐。
     *
     * 命中后写入 path 调用方接收的 absBoxes 必须与 mx/my 同坐标系
     * （root 局部），否则下游 ripple / slider 等用 mx - absBox[0] 算的相对位置会错。
     * 因此把递归得到的屏幕坐标 absBox 减回 rootOffset，统一为 root 局部坐标。
     */
    private fun hitModalSubtree(
        modal: Modal,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        path: MutableList<UIElement<*>>,
        absBoxes: MutableList<FloatArray>?,
    ): Boolean {
        // fullscreen modal 用屏幕坐标系；非 fullscreen modal 自身居中后 lastRenderedOrigin 已是屏幕坐标
        // 一律把入口 mx/my 加回 rootOffset 转屏幕坐标，与 modal 子节点位置同坐标系
        val roX = rootOffsetX()
        val roY = rootOffsetY()
        val screenMx = mx + roX
        val screenMy = my + roY
        val rendered = modal.lastRenderedOrigin()
        val modalBox = layout.boxOf(modal)
        val originX = rendered?.getOrNull(0)
            ?: (modalBox?.x() ?: 0f)
        val originY = rendered?.getOrNull(1)
            ?: (modalBox?.y() ?: 0f)
        // 遍历前取快照：命中遍历期间响应式 Effect 重建可能 replaceChildren 修改 modal 的 live children，
        // 直接迭代会抛 ConcurrentModificationException（与 UIRenderer.childrenSnapshot 同理）。
        for (child in modal.children().toList()) {
            val childPath = ArrayList<UIElement<*>>()
            val childBoxes: MutableList<FloatArray>? = if (absBoxes == null) null else ArrayList()
            if (
                hit(
                    child, layout, originX, originY,
                    Transform2D.IDENTITY, AffineMatrix3.IDENTITY,
                    screenMx, screenMy, childPath, childBoxes,
                )
            ) {
                path.addAll(childPath)
                if (absBoxes != null && childBoxes != null) {
                    // 把屏幕坐标 absBox 减回 rootOffset 转为 root 局部坐标，与入口 mx/my 同坐标系
                    for (childBox in childBoxes) {
                        if (childBox.size >= 2) {
                            childBox[0] -= roX
                            childBox[1] -= roY
                        }
                        absBoxes.add(childBox)
                    }
                }
                return true
            }
        }
        return false
    }

    /** 非 passthrough modal 的 backdrop 处理：吸收事件，可选触发 dismiss。
     * 写入 absBoxes 时把屏幕坐标 origin 减回 rootOffset，与入口 mx/my 同坐标系（root 局部）。
     */
    private fun handleNonPassthroughBackdrop(
        modal: Modal,
        layout: LayoutResult,
        path: MutableList<UIElement<*>>,
        absBoxes: MutableList<FloatArray>?,
    ) {
        val rendered = modal.lastRenderedOrigin()
        val modalBox = layout.boxOf(modal)
        val originX = rendered?.getOrNull(0)
            ?: (modalBox?.x() ?: 0f)
        val originY = rendered?.getOrNull(1)
            ?: (modalBox?.y() ?: 0f)
        // rendered 数组若有 4 元素则用其宽高；否则回落到 layout box
        val w = if (rendered != null && rendered.size >= 4) {
            rendered[2]
        } else {
            (modalBox?.width() ?: 0f)
        }
        val h = if (rendered != null && rendered.size >= 4) {
            rendered[3]
        } else {
            (modalBox?.height() ?: 0f)
        }
        // dismissOnOutsideClick=true 才把 modal 加入命中路径触发 onDismiss；
        // 否则直接吸收事件（path 为空），避免 onDismiss handler 在 dismissOnOutsideClick=false 时被错误触发
        if (!modal.dismissOnOutsideClick()) {
            return
        }
        path.add(modal)
        if (absBoxes != null) {
            // 减回 rootOffset：与 mx/my (root 局部) 同坐标系
            absBoxes.add(
                floatArrayOf(
                    originX - rootOffsetX(),
                    originY - rootOffsetY(),
                    w,
                    h,
                ),
            )
        }
    }

    /**
     * 判断点击是否落在 spotlight 的「洞」内（即被高亮的目标元素 + padding 扩展区）。
     *
     * 语义：返回 true 表示点击穿透到主树命中目标按钮；返回 false 表示
     * 落在遮罩区域应吸收事件不穿透；返回 null 表示找不到 spotlight 目标元素，
     * 调用方按「洞外」保守处理。
     *
     * 入口 mx/my 是 root 局部坐标，洞几何 = 目标元素 root 局部坐标 ± padding——同坐标系。
     */
    private fun isInsideSpotlightHole(
        modal: Modal,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        root: UIElement<*>,
    ): Boolean? {
        val targetId = modal.spotlightTargetId()
        if (targetId.isNullOrEmpty()) return null
        // 1. 优先在本 root 树 DFS：与渲染侧 drawSpotlightMask 同一份查找逻辑，
        //    保证视觉遮罩洞与可点击洞几何完全一致（target 是 root 局部坐标）。
        val target: FloatArray? = overlayOrchestrator().findModalElementBox(root, layout, targetId, 0f, 0f)
        val padding = modal.spotlightPadding()
        if (target != null) {
            // 本 root 命中：mx/my 是 root 局部坐标，target 也是 root 局部，同坐标系比较。
            val holeX = target[0] - padding
            val holeY = target[1] - padding
            val holeRight = target[0] + target[2] + padding
            val holeBottom = target[1] + target[3] + padding
            return mx >= holeX && mx < holeRight && my >= holeY && my < holeBottom
        }
        // 2. 本 root 找不到：回落全局 elementBox 查询（屏幕绝对坐标）。
        //    把 mx/my 加回真实 rootOffset 转屏幕坐标后与屏幕坐标系下的洞比较。
        val globalTarget = lookupElementBoxGlobal(targetId) ?: return null
        val screenMx = mx + rootOffsetX()
        val screenMy = my + rootOffsetY()
        val holeX = globalTarget[0] - padding
        val holeY = globalTarget[1] - padding
        val holeRight = globalTarget[0] + globalTarget[2] + padding
        val holeBottom = globalTarget[1] + globalTarget[3] + padding
        return screenMx >= holeX && screenMx < holeRight && screenMy >= holeY && screenMy < holeBottom
    }

    // ===== 递归命中测试 =====

    /**
     * 递归命中测试，将命中路径写入 path。
     * parentMatrix 表示从 parentX/Y 坐标系到屏幕坐标系的仿射变换。
     * 元素在该坐标系中的位置由 box.x/y 给出，加上元素自身的 runtime 变换。
     */
    @Suppress("CyclomaticComplexMethod", "ReturnCount")
    private fun hit(
        node: UIElement<*>,
        layout: LayoutResult,
        parentX: Float,
        parentY: Float,
        parentTransform: Transform2D,
        parentMatrix: AffineMatrix3,
        mx: Double,
        my: Double,
        path: MutableList<UIElement<*>>,
        absBoxes: MutableList<FloatArray>?,
    ): Boolean {
        // pointerEvents == false 跳过整棵子树
        if (!node.pointerEvents()) {
            return false
        }
        // M6-f §8.2：visible=false 也跳过命中（自身及子树都不响应事件）
        if (!node.visible()) {
            return false
        }
        val box: Box = layout.boxOf(node) ?: return false
        val absX = parentX + box.x()
        val absY = parentY + box.y()
        val transform = parentTransform.push(node)

        // 累积矩阵 = parentMatrix × 元素自身 runtime 变换
        // 与 Transform2D 一致：先在父坐标系下定位（parentX+box.x 已包含在 absX/absY），
        // 然后叠加元素自身 runtime 变换产生子坐标系到屏幕的映射。
        val currentMatrix = parentMatrix.multiply(buildLocalTransformOnly(node))

        // 透明布局容器（display: contents，如 $for/$cond）：自身不生成命中盒，纯穿透。
        // 布局引擎给被摊平的透明容器合成的盒偏移恒 (0,0)、尺寸覆盖父内容区，若按普通容器
        // 以该盒做命中闸门，会在子节点倒序遍历时抢先命中、并因下方无条件 return true 而吞掉
        // DOM 中排在它之前的兄弟（如同列的 header）的点击。故此处只把命中机会按 子→父 冒泡
        // 转交其子：自身不入 path、不做闸门，无子命中则返回 false 让父继续试更早的兄弟。
        // 偏移恒 0 使子节点（坐标已相对真实父）经 childOrigin 透传后绝对坐标正确。
        // 注：未被摊平的透明容器（STACK/GRID/ABSOLUTE 父或滚动容器下，仍有真实盒）也走本分支——
        // 其子坐标相对该容器，absX 已含容器真实盒偏移，childOrigin 透传后坐标同样正确；纯穿透
        // 对它也是正确语义（透明容器无视觉无 handler，不应吸收落在子项空隙的点击）。
        if (node.transparentLayout()) {
            val passOriginX = HitGeometry.childOriginX(node, absX)
            val passOriginY = HitGeometry.childOriginY(node, absY)
            // 遍历前取快照：命中遍历期间响应式 Effect 重建可能 replaceChildren 修改 live children，
            // 直接迭代会抛 ConcurrentModificationException（与 UIRenderer.childrenSnapshot 同理）。
            val passChildren = node.children().toList()
            for (i in passChildren.indices.reversed()) {
                if (
                    hit(
                        passChildren[i], layout, passOriginX, passOriginY,
                        transform, currentMatrix, mx, my, path, absBoxes,
                    )
                ) {
                    return true
                }
            }
            return false
        }

        var effectiveHeight = box.height()
        if (node is Dropdown && node.expanded()) {
            effectiveHeight += node.options().size * node.effectiveOptionHeight()
        }

        // 命中测试：将屏幕鼠标坐标逆变换到当前坐标系后，与绝对矩形比对
        if (
            !currentMatrix.invertedHitTest(
                mx.toFloat(),
                my.toFloat(),
                absX,
                absY,
                box.width(),
                effectiveHeight,
            )
        ) {
            return false
        }

        path.add(node)
        if (absBoxes != null) {
            absBoxes.add(transformedBox(transform, absX, absY, box.width(), box.height()))
        }

        val childOriginX = HitGeometry.childOriginX(node, absX)
        val childOriginY = HitGeometry.childOriginY(node, absY)
        // 子节点倒序遍历：渲染层正序绘制（后画在上层），命中测试应让上层优先命中。
        // 例如 vanilla 背包 root 下 background image 在前、slots 在后；正序遍历会让
        // image 先命中并 return true，slot 永远命中不到。
        // 遍历前取快照：命中遍历期间响应式 Effect 重建可能 replaceChildren 修改 live children，
        // 直接迭代会抛 ConcurrentModificationException（与 UIRenderer.childrenSnapshot 同理）。
        val children = node.children().toList()
        for (i in children.indices.reversed()) {
            val child = children[i]
            if (
                hit(
                    child, layout, childOriginX, childOriginY,
                    transform, currentMatrix, mx, my, path, absBoxes,
                )
            ) {
                return true
            }
        }
        return true
    }

    // ===== Dropdown Overlay 命中 =====

    @JvmStatic
    fun hitDropdownOverlay(
        root: UIElement<*>,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        path: MutableList<UIElement<*>>,
        absBoxes: MutableList<FloatArray>?,
    ): Boolean {
        val result = HitResult()
        // 先在 modal 子树内找展开面板：modal 用 lastRenderedOrigin 渲染（屏幕坐标），而入口 mx/my 是
        // root 局部坐标（已减去 rootOffset）。与 hitModalSubtree 同口径——把 mx/my 加回 rootOffset 还原
        // 屏幕坐标、用 modal 渲染原点遍历子树，命中后再把 box 减回 rootOffset 转 root 局部，使下游
        // applySpecialClick 的 option 命中与入口 mx/my 同坐标系。否则按 modal 原始布局坐标会命中错位。
        val modals = overlayOrchestrator().collectVisibleModals(root)
        if (modals.isNotEmpty()) {
            val roX = rootOffsetX()
            val roY = rootOffsetY()
            val screenMx = mx + roX
            val screenMy = my + roY
            var i = modals.size - 1
            while (i >= 0 && result.element == null) {
                val modal = modals[i]
                val rendered = modal.lastRenderedOrigin()
                val modalBox = layout.boxOf(modal)
                val originX = rendered?.get(0) ?: (modalBox?.x() ?: 0f)
                val originY = rendered?.get(1) ?: (modalBox?.y() ?: 0f)
                for (child in modal.children().toList()) {
                    findDropdownOverlayHit(
                        child,
                        layout,
                        originX,
                        originY,
                        Transform2D.IDENTITY,
                        screenMx,
                        screenMy,
                        result,
                    )
                    if (result.element != null) break
                }
                i--
            }
            val hit = result.element
            if (hit != null) {
                result.box?.let { b ->
                    if (b.size >= 2) {
                        b[0] -= roX
                        b[1] -= roY
                    }
                }
                path.add(hit)
                if (absBoxes != null) {
                    result.box?.let { absBoxes.add(it) }
                }
                return true
            }
        }
        // 主 root 树（root 局部坐标）。findDropdownOverlayHit 内部跳过 Modal 子树，
        // 避免按 modal 原始布局坐标重复命中（modal 子树已在上方按屏幕坐标处理）。
        findDropdownOverlayHit(root, layout, 0f, 0f, Transform2D.IDENTITY, mx, my, result)
        val element = result.element ?: return false
        path.add(element)
        if (absBoxes != null) {
            result.box?.let { absBoxes.add(it) }
        }
        return true
    }

    private fun findDropdownOverlayHit(
        node: UIElement<*>,
        layout: LayoutResult,
        parentX: Float,
        parentY: Float,
        parentTransform: Transform2D,
        mx: Double,
        my: Double,
        result: HitResult,
    ) {
        // Modal 子树用屏幕坐标渲染，由 hitDropdownOverlay 入口单独按 lastRenderedOrigin 处理；
        // 主树遍历到 Modal 时跳过，防止按原始布局坐标重复且错位地命中其内 dropdown。
        if (node is Modal) return
        val box: Box = layout.boxOf(node) ?: return
        val absX = parentX + box.x()
        val absY = parentY + box.y()
        val transform = parentTransform.push(node)
        val headerBox = transformedBox(transform, absX, absY, box.width(), box.height())
        if (node is Dropdown &&
            node.expanded() &&
            isInDropdownPanel(node, headerBox, mx, my)
        ) {
            result.element = node
            result.box = headerBox
        }

        val childOriginX = HitGeometry.childOriginX(node, absX)
        val childOriginY = HitGeometry.childOriginY(node, absY)
        // 与 hit() 保持一致：子节点倒序遍历，让上层优先命中。
        // 遍历前取快照：命中遍历期间响应式 Effect 重建可能 replaceChildren 修改 live children，
        // 直接迭代会抛 ConcurrentModificationException（与 UIRenderer.childrenSnapshot 同理）。
        val children = node.children().toList()
        for (i in children.indices.reversed()) {
            findDropdownOverlayHit(
                children[i],
                layout,
                childOriginX,
                childOriginY,
                transform,
                mx,
                my,
                result,
            )
        }
    }

    private fun isInDropdownPanel(d: Dropdown, headerBox: FloatArray, mx: Double, my: Double): Boolean {
        if (d.options().isEmpty()) {
            return false
        }
        val panelTop = headerBox[1] + headerBox[3]
        val optionHeight = d.effectiveOptionHeight() * HitGeometry.scaleY(headerBox)
        val panelBottom = panelTop + d.options().size * optionHeight
        return mx >= headerBox[0] && mx < headerBox[0] + headerBox[2] && my >= panelTop && my < panelBottom
    }

    // ===== 几何工具 =====

    private fun transformedBox(transform: Transform2D, x: Float, y: Float, width: Float, height: Float): FloatArray =
        transform.transformedBox(x, y, width, height)

    /**
     * 从元素的 runtime 变换属性构建局部变换矩阵（委托到 [AffineMatrix3.fromElement]）。
     */
    private fun buildLocalTransformOnly(el: UIElement<*>): AffineMatrix3 = AffineMatrix3.fromElement(el)

    // ===== 内部类型 =====

    private class HitResult {
        var element: UIElement<*>? = null
        var box: FloatArray? = null
    }
}
